import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { PlanSubscription } from '@tv3/store/plan/plan.model'
import { Destroyable, searchTable, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import * as moment from 'moment'
import { EMPTY, Observable } from 'rxjs'
import { catchError, filter, map, switchMapTo } from 'rxjs/operators'
import { WebreadySubscription } from '../store/app/app-sub.model'
import { TokeetApp } from '../store/app/app.model'
import { AppService } from '../store/app/app.service'
import { SubscribedAppsStore } from './subscribed-apps.store'
import { Card } from '../store/card/card.model'

@Component({
  selector: 'app-subscribed-apps',
  templateUrl: './subscribed-apps.component.html',
  styleUrls: ['./subscribed-apps.component.scss'],
  providers: [SubscribedAppsStore],
})
export class SubscribedAppsComponent extends Destroyable implements OnInit, OnChanges {
  @Input() subscriptions: PlanSubscription[] | null = null
  @Input() card: Card | null = null

  @ViewChild('table') appTable: ElementRef<HTMLTableElement>

  subscriptions$ = this.store.selectSubscriptions$
  subscriptionsLoading$ = this.store.selectSubscriptionsLoading$
  apps$ = this.store.selectApps$
  webreadySubscription$ = this.store.selectWebreadySubscription$
  rgPrice$ = this.store.selectRGPrice$

  unsubscribingWebreadyAddons: string
  private rateGeniePricesLoaded = false

  constructor(private store: SubscribedAppsStore, private appsService: AppService, private toaster: Toaster) {
    super()
  }

  ngOnInit() {
    // If subscriptions are provided as input, use them; otherwise fetch fresh
    if (this.subscriptions) {
      this.store.setAccount({ subscriptions: this.subscriptions })
      // Set loading to false since we have the data
      this.store.setLoading({ key: 'subscriptions', value: false })
    }

    // Load apps and webready subscription
    this.store.loadApps(this.appsService.getApps())
    this.store.loadWebreadySubscription(this.appsService.getWebreadySubscription())

    // Only load RateGenie prices if user has RateGenie subscription
    this.subscriptions$
      .pipe(
        filter((subscriptions) => subscriptions !== null),
        untilDestroy(this)
      )
      .subscribe((subscriptions: PlanSubscription[]) => {
        const hasRateGenieSubscription = subscriptions.some((s) => s.planId === 'rategenie_v3')
        if (hasRateGenieSubscription && !this.rateGeniePricesLoaded) {
          this.rateGeniePricesLoaded = true
          this.store.loadRGPrices(this.appsService.getRategeniePrices())
        }
      })
  }

  ngOnChanges(changes: SimpleChanges) {
    // Update subscriptions when input changes
    if (changes['subscriptions'] && this.subscriptions) {
      this.store.setAccount({ subscriptions: this.subscriptions })
      // Set loading to false since we have the data
      this.store.setLoading({ key: 'subscriptions', value: false })
    }
  }

  geWebreadyLogo(apps$: Observable<{ [index: string]: TokeetApp }>, feature: string) {
    return apps$.pipe(
      map((apps: any) => {
        if (feature === 'multi_rental_website' || feature === 'single_rental_website') {
          return apps.webready?.logo_url
        } else {
          return apps[feature]?.logo_url || apps.webready?.logo_url
        }
      })
    )
  }

  isWebreadyAddon(product: any) {
    return this.appsService.isWebreadyAddon(product)
  }

  isOverdue(end: number) {
    const now = moment.utc().unix()
    return now > end
  }

  roundFloat(price: number) {
    return parseFloat((Math.round(price * 100) / 100).toFixed(2))
  }

  isActive(subscription: PlanSubscription) {
    return (subscription.status === 'active' || subscription.status === 'trialing') && !subscription.cancelAtPeriodEnd
  }

  isActiveWebreadyAddon(subscription: WebreadySubscription, product: string) {
    return (
      !subscription.cancel_at_period_end &&
      subscription.features[product]?.active &&
      (!subscription.next_billing_cycle || subscription.next_billing_cycle.features[product]?.active)
    )
  }

  getStatusWebreadyAddon(subscription: WebreadySubscription, product: string) {
    if (
      !subscription.cancel_at_period_end &&
      subscription.features[product]?.active &&
      (!subscription.next_billing_cycle || subscription.next_billing_cycle.features[product]?.active)
    ) {
      return $localize`Active`
    } else if (
      subscription.cancel_at_period_end ||
      (subscription.features[product]?.active &&
        !!subscription.next_billing_cycle &&
        !subscription.next_billing_cycle.features[product]?.active)
    ) {
      return $localize`Cancelled`
    } else {
      return $localize`Inactive`
    }
  }

  getStatus(subscription: PlanSubscription) {
    if (subscription.cancelAtPeriodEnd) {
      return $localize`Cancelled`
    } else if (subscription.status === 'active') {
      return $localize`Active`
    } else if (subscription.status === 'trialing') {
      return $localize`Trial`
    } else if (subscription.status === 'past_due') {
      return $localize`Past Due`
    } else {
      return $localize`Inactive`
    }
  }

  getCharge(featureKey: string, featureValue: any) {
    const round = (price) => parseFloat((Math.round(price) / 100).toFixed(2))

    switch (featureKey) {
      case 'webready_edocuments':
        return `$${round(featureValue.total)} ` + $localize`for` + ` ${featureValue.quantity} ` + $localize`document(s)`
      case 'webready_invoicing':
        return $localize`Fee of 1% per invoice`
      case 'webready':
        return `$${round(featureValue.total)} ` + $localize`for` + ` ${featureValue.quantity} ` + $localize`rental(s)`
      case 'webready_hosting_fee':
        if (featureValue.quantity > 0) {
          return (
            `$${round(featureValue.total)} ` + $localize`for` + ` ${featureValue.quantity} ` + $localize`website(s)`
          )
        } else {
          return $localize`$0 for no website(s)`
        }
      case 'booking_engine':
      case 'webready_messaging':
      case 'webready_automations':
      case 'webready_airbnb_connector':
        return `$${round(featureValue.price)} ` + $localize`per month`
      case 'webready_translator':
      case 'single_rental_website':
      case 'multi_rental_website':
        if (featureValue.quantity > 0) {
          return (
            `$${round(featureValue.price * featureValue.quantity)} ` +
            $localize`for` +
            ` ${featureValue.quantity} ` +
            $localize`website(s)`
          )
        } else {
          return $localize`$0 for no website(s)`
        }
      case 'tokeet_smart_devices':
        return (
          `$${round(featureValue.price * featureValue.quantity)} ` +
          $localize`for` +
          ` ${featureValue.quantity} ` +
          $localize`device(s)`
        )
    }
  }

  getSubscriptionPriceUnit(type: string, isYearly = false) {
    switch (type) {
      case 'flat':
      case 'per-owner':
      case 'per-rental':
        return isYearly ? $localize`per year` : $localize`per month`
      case 'rental-brackets':
        return isYearly ? $localize`per year` : $localize`per month`
      case 'per-job':
        return $localize`Variable price per job`
      case 'per-website':
        return $localize`Variable price per website`
      case 'per-document':
        return $localize`Variable price per document`
      case 'percent':
        return $localize`Variable percentage of sales`
    }
  }

  getWebreadyStatus(subscription: WebreadySubscription) {
    if (subscription.cancel_at_period_end) {
      return $localize`Cancelled`
    } else if (subscription.status === 'active') {
      return $localize`Active`
    } else if (subscription.delinquent) {
      return $localize`Past Due`
    } else {
      return $localize`Inactive`
    }
  }

  onWebreadyAddonStatusChange(checked: boolean, product: string) {
    if (checked) {
      return
    }
    this.unsubscribingWebreadyAddons = product
    this.appsService
      .unsubscribeWebreadyAddon(product)
      .pipe(
        switchMapTo(this.appsService.getWebreadySubscription()),
        catchError(() => {
          this.toaster.error($localize`Unable to unsubscribe addon.`)
          this.unsubscribingWebreadyAddons = null
          return EMPTY
        })
      )
      .subscribe((subscription) => {
        this.toaster.success($localize`Addon unsubscribed successfully`)
        this.store.loadWebreadySubscription(subscription)
        this.unsubscribingWebreadyAddons = null
      })
  }

  onStatusChange(checked: boolean, subscription: PlanSubscription) {
    if (checked) {
      this.appsService.subscribe(subscription.product, subscription.planId).subscribe((result) => {
        this.toaster.success($localize`Subscribed to addon successfully`)
        this.store.updateSubscription(result.subscription)
      })
    } else {
      this.appsService.unsubscribe(subscription.planId).subscribe((subscriptions) => {
        this.toaster.success($localize`Unsubscribed from addon successfully`)
        this.store.setSubscriptions(subscriptions)
      })
    }
  }

  isMonthly(subscription: PlanSubscription) {
    return !(subscription.planId + '').includes('_yr')
  }

  isYearly(subscription: PlanSubscription) {
    return (subscription.planId + '').includes('_yr')
  }

  isQuarterly(subscription: PlanSubscription) {
    return (subscription.planId + '').includes('_yr_q_')
  }

  isBiyearly(subscription: PlanSubscription) {
    return (subscription.planId + '').includes('_yr_by_')
  }

  getDisplayPrice(subscription: PlanSubscription) {
    if ((subscription.planId + '').includes('_yr_q_')) {
      return subscription.price * 4
    }
    if ((subscription.planId + '').includes('_yr_by_')) {
      return subscription.price * 2
    }
    return subscription.price
  }

  onSearch(term: string) {
    searchTable(this.appTable.nativeElement, term)
  }
}
